import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { type IConversation, ReadStatus } from 'src/api/landlord-messages-api';
import {
    EMessageChannel,
    isMessageChannel,
    useLead,
    useLoggedInUser,
    useLeadMessages,
    useShowings,
    lastThirtyDays,
    useUpdateConversationsReadStatus,
    useFacebookIntegration,
    useProperty,
    useTeamSettings,
    useSnippets,
    useLeadStageHistory,
} from 'src/services/api';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import { isUserAway } from 'src/api/users-api';
import { useFlags } from '@flagsmith/flagsmith/react';
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';
import { LeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/context';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { LeadUtils } from 'src/utils/LeadUtils';
import { searchSnippets } from 'src/pages/landlord/leads/utils/search-snippets';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { detectLeadLanguage } from 'src/pages/landlord/leads/messages/utils/detect-lead-language';
import { useIsLive } from 'src/services/websocket/use-is-live';
import { useLiveRefetch } from 'src/services/websocket/use-live-refetch';

const basePath = '/landlord/inner/messages/from-lead';

const getConversationPath = (channel?: EMessageChannel, leadUuid?: string, backPath?: string) => {
    if (!leadUuid || !channel) {
        return;
    }

    let path = `${basePath}?lead_uuid=${leadUuid}&channel=${channel}&show_navigation=true`;
    if (backPath) {
        path += `&back_path=${encodeURIComponent(backPath)}`;
    }
    return path;
};

const REFETCH_INTERVAL = 3000;

type Props = {
    children: React.ReactNode;
};

export default function LeadMessagesPageProvider({ children }: Props) {
    const [searchParams] = useSearchParams();
    const leadUuid = searchParams.get('lead_uuid');
    const channel = searchParams.get('channel');
    const showNavigation = Boolean(searchParams.get('show_navigation'));

    if (!leadUuid) {
        console.error('Lead UUID must be set in URL params');

        return (
            <Alert severity="warning">
                Lead UUID must be set
            </Alert>
        );
    }

    let validChannel: EMessageChannel | undefined;
    if (channel) {
        if (!isMessageChannel(channel)) {
            console.error(`Invalid send channel: ${channel}`);

            return (
                <Alert severity="warning">
                    Invalid message channel: {channel}.
                    It must be either {EMessageChannel.SMS}, {EMessageChannel.EMAIL} or {EMessageChannel.FACEBOOK_MESSENGER}.
                </Alert>
            );
        }
        validChannel = channel;
    }

    return (
        <ValidLeadMessagesPageProvider
            leadUuid={leadUuid}
            channel={validChannel}
            showNavigation={showNavigation}
        >
            {children}
        </ValidLeadMessagesPageProvider>
    );
}

type ValidProviderProps = Props & {
    leadUuid: string;
    channel?: EMessageChannel;
    showNavigation: boolean;
};

function ValidLeadMessagesPageProvider({
    children,
    leadUuid,
    channel,
    showNavigation,
}: ValidProviderProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: facebookIntegration } = useFacebookIntegration();

    const setChannel = (newChannel: EMessageChannel, replace = false) => {
        setSearchParams(prev => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set('channel', newChannel);
            return newSearchParams.toString();
        }, { replace });
    };

    const isLive = useIsLive();
    const { data: loggedInUser } = useLoggedInUser();
    const userSettings = loggedInUser?.settings;
    const { data: teamSettings } = useTeamSettings();
    const flags = useFlags([EFeatureFlag.TEAM_AVAILABILITY]);
    const isAvailabilityEnabled = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;
    const { data: teamMembersWithPresence } = useTeamMembers({ includeInactive: true, refetchInterval: isAvailabilityEnabled ? 60_000 : false });
    const { data: snippets } = useSnippets();
    const { data: showings } = useShowings({
        starts_after: lastThirtyDays(),
    });
    const { data: lead } = useLead(leadUuid);
    const { data: property } = useProperty(lead?.property_id, { include_facts: true });
    const { data: messagesResponse, refetch: refetchMessages } = useLeadMessages(
        leadUuid,
        { refetchInterval: isLive ? false : REFETCH_INTERVAL, include_attachments: true }
    );
    useLiveRefetch({
        isLive,
        enabled: true,
        refetch: refetchMessages,
        shouldRefetchFrame: (frame) => (
            frame.type === 'message.created' && frame.lead_uuid === leadUuid
        ),
    });
    const { data: stageHistory } = useLeadStageHistory(leadUuid);
    const stages = stageHistory?.stages ?? [];
    const interests = stageHistory?.interests ?? [];
    const messages = messagesResponse?.messages;
    const attachments = messagesResponse?.attachments;

    const { mutateAsync: updateReadStatus } = useUpdateConversationsReadStatus();

    const [isSnippetsModalOpen, setIsSnippetsModalOpen] = useState(false);

    // Search state for snippets
    const [search, setSearch] = useState('');

    const [conversations, saveConversationsToLocalStorage] = usePersistentState(
        'conversations',
        [] as IConversation[],
        (value): value is IConversation[] => Array.isArray(value)
    );

    // Reset the conversations in local storage if the `show_navigation` param is not present
    useEffect(() => {
        if (!showNavigation) {
            saveConversationsToLocalStorage([]);
        }
    }, [showNavigation]);

    const [isTranslateOutboundEnabled, setIsTranslateOutboundEnabled] =
        usePersistentState<boolean | null>(
            `translate-outbound-${leadUuid}`,
            null,
            (v): v is boolean | null => v === null || typeof v === 'boolean'
        );

    const detectedLeadLang = useMemo(
        () => (messages ? detectLeadLanguage(messages) : null),
        [messages]
    );


    const filteredSnippets = useMemo(() => {
        const _filteredSnippets = snippets || [];
        if (!search) {
            return _filteredSnippets;
        }

        return searchSnippets(_filteredSnippets, search);
    }, [snippets, search]);

    const currentConversationIndex = conversations.findIndex((o) => o.lead_uuid === leadUuid);
    const previousConversation = conversations[currentConversationIndex - 1];
    const nextConversation = conversations[currentConversationIndex + 1];
    const conversationAfterNextConversation = conversations[currentConversationIndex + 2];

    // Prefetch the previous and next lead's messages
    useLead(previousConversation?.lead_uuid);
    useLeadMessages(previousConversation?.lead_uuid, { include_attachments: true });
    useProperty(previousConversation?.property_id);
    useLead(nextConversation?.lead_uuid);
    useLeadMessages(nextConversation?.lead_uuid, { include_attachments: true });
    useProperty(nextConversation?.property_id);
    useLead(conversationAfterNextConversation?.lead_uuid);
    useLeadMessages(conversationAfterNextConversation?.lead_uuid, { include_attachments: true });
    useProperty(conversationAfterNextConversation?.property_id);

    const previousLead = useLead(previousConversation?.lead_uuid)?.data;
    const nextLead = useLead(nextConversation?.lead_uuid)?.data;

    const backPath = searchParams.get('back_path') ?? undefined;
    const previousConversationPath = getConversationPath(previousConversation?.latest_message_channel, previousLead?.uuid, backPath);
    const nextConversationPath = getConversationPath(nextConversation?.latest_message_channel, nextLead?.uuid, backPath);

    const navigateToPreviousConversation = () => {
        if (previousConversationPath) {
            navigate(previousConversationPath);
        }
    };

    const navigateToNextConversation = () => {
        if (nextConversationPath) {
            navigate(nextConversationPath);
        }
    };

    useEffect(() => {
        updateReadStatus({
            leadUuids: [leadUuid],
            status: ReadStatus.READ,
        });
    }, [leadUuid, messages]);

    // Default the channel when none is in the URL (e.g. links from the tours
    // calendar or analytics that only pass lead_uuid). Use replace so this
    // auto-added param does not create an extra history entry that traps the
    // browser back button on the messages page.
    useEffect(() => {
        if (!channel && lead && messages) {
            setChannel(LeadUtils.getDefaultMessageChannel(lead, messages), true);
        }
    }, [channel, lead, messages]);

    const isOwnerAway = isAvailabilityEnabled &&
        isUserAway(teamMembersWithPresence?.find(m => m.id === lead?.owner));

    const isLoading = !loggedInUser || !userSettings || !teamSettings || !snippets || (lead?.property_id && !property) || !showings || !lead || !messages || !stageHistory;
    if (isLoading || !channel) {
        return (
            <LinearProgress />
        );
    }

    return (
        <LeadMessagesPageContext.Provider value={{
            loggedInUser,
            userSettings,
            teamSettings,
            showings,
            snippets: filteredSnippets,
            stages,
            interests,
            teamMembers: teamMembersWithPresence || [],
            isFacebookConnected: !!facebookIntegration?.user_integration,
            isOwnerAway,
            isAvailabilityEnabled,

            lead,
            property,
            messages,
            attachments,

            channel,
            setChannel,
            showNavigation,

            conversations,
            previousLead,
            navigateToPreviousConversation,
            nextLead,
            navigateToNextConversation,

            detectedLeadLang,
            isTranslateOutboundEnabled,
            setIsTranslateOutboundEnabled,

            isSnippetsModalOpen,
            setIsSnippetsModalOpen,

            search,
            setSearch,
        }}
        >
            {children}
        </LeadMessagesPageContext.Provider>
    );
}
